import { useCallback, useEffect, useState } from 'react';
import { Crown } from 'lucide-react';
import MappingList from '../../../components/MappingList';
import {
	listShippingMethodMappings,
	addShippingMethodMapping,
	updateShippingMethodMapping,
	removeShippingMethodMapping,
	listShipViaOptions,
} from '../../../utils/queries';
import { useCan } from '../../../hooks/useCan';
import { PLATFORMADMIN_ROLES } from '../../../components/navConfig';

const SHIPPING_COLUMNS_BASE = [
	{ key: 'shippingTitle', header: 'Shipping Title' },
	{ key: 'shippingCode', header: 'Shipping Code' },
	{ key: 'shippingSource', header: 'Source' },
	// Used on all orders when none is provided or none of the order's
	// shipping_lines match another mapping (see customerMapping.service.ts).
	{
		key: 'defaultFlag',
		header: 'Default',
		render: (value) => (value ? <Crown size={20} className='text-green-600' /> : ''),
	},
];

const SHIPPING_ROW_ACTIONS = [
	{
		label: 'Mark Default',
		icon: Crown,
		hidden: (row) => row.defaultFlag,
		onClick: (row) => updateShippingMethodMapping(row.shippingMethodMappingId, { defaultFlag: true }),
	},
];
const SHIPPING_ADD_FIELDS_BASE = [
	{
		key: 'shippingTitle',
		label: 'Shipping Title',
		hint: "A display label for this mapping. Not shown to customers or members — it's only used to match an order if Shipping Code/Source don't.",
	},
	{
		key: 'shippingCode',
		label: 'Shipping Code',
		hint: 'Must exactly match the shipping code the store\'s checkout sends on the order. If the storefront always sends a fixed code (e.g. "00"), enter that.',
	},
	{
		key: 'shippingSource',
		label: 'Source',
		hint: 'Must exactly match the shipping source the store\'s checkout sends on the order. If the storefront always sends a fixed source (e.g. "portal"), enter that.',
	},
];

export default function ShippingMethodsTab({ storeId }) {
	const canEdit = useCan(PLATFORMADMIN_ROLES);
	const listShipping = useCallback(() => listShippingMethodMappings(storeId), [storeId]);
	const addShipping = useCallback((fields) => addShippingMethodMapping(storeId, fields), [storeId]);

	const [shipViaOptions, setShipViaOptions] = useState([]);
	useEffect(() => {
		listShipViaOptions(storeId).then((result) => {
			if (result?.success) setShipViaOptions(result.data);
		});
	}, [storeId]);

	const shipViaDescriptions = shipViaOptions.reduce((acc, o) => {
		acc[String(o.id)] = o.description;
		return acc;
	}, {});

	const columns = [
		...SHIPPING_COLUMNS_BASE,
		{
			key: 'erpShipViaId',
			header: 'ERP Ship Via ID',
			render: (value) => (shipViaDescriptions[value] ? `${shipViaDescriptions[value]} (${value})` : value),
		},
	];

	const addFields = [
		...SHIPPING_ADD_FIELDS_BASE,
		{
			key: 'erpShipViaId',
			label: 'ERP Ship Via ID',
			required: true,
			type: 'select',
			hint: 'The PACE carrier this shipping option ships via. Selected from PACE’s live Ship Via catalog.',
			options: shipViaOptions.map((o) => ({ value: o.id, label: `${o.description} (${o.id})` })),
		},
	];

	return (
		<div>
			<h2 className='text-base font-medium mb-4'>Shipping Method Mappings</h2>
			<MappingList
				list={listShipping}
				add={addShipping}
				remove={removeShippingMethodMapping}
				columns={columns}
				addFields={addFields}
				rowActions={SHIPPING_ROW_ACTIONS}
				idKey='shippingMethodMappingId'
				emptyHeading='No shipping method mappings yet'
				collapsibleAdd
				canEdit={canEdit}
			/>
		</div>
	);
}
